'use client';

import React, { ReactNode, useRef, useEffect } from 'react';
import { Drawer as VaulDrawer } from 'vaul';
import { cn } from '@/lib/utils';

// Visually hidden styles for accessibility
const visuallyHiddenStyles: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: '0',
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: '0',
}

interface TopDrawerProps {
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  className?: string;
  translucent?: boolean;
}

export function TopDrawer({
  children,
  open,
  onOpenChange,
  className,
  translucent = false,
}: TopDrawerProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Move focus to the drawer content when it opens to avoid aria-hidden issues
  useEffect(() => {
    if (open && contentRef.current) {
      // Small delay to ensure the drawer is rendered
      const timer = setTimeout(() => {
        contentRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <VaulDrawer.Root
      direction="top"
      shouldScaleBackground={false}
      open={open}
      onOpenChange={onOpenChange}
      modal={true}
    >
      <VaulDrawer.Portal>
        <VaulDrawer.Overlay 
          className='fixed inset-0 z-50 bg-transparent' 
          onClick={() => onOpenChange(false)}
        />
        <VaulDrawer.Content 
          ref={contentRef}
          className={cn(
            'fixed top-0 left-0 right-0 z-50 w-full h-auto max-h-[90vh] md:w-auto md:max-w-sm md:left-auto md:right-0 md:h-auto md:max-h-screen text-foreground rounded-b-2xl md:rounded-bl-2xl md:rounded-br-none border border-border/50 shadow-lg focus:outline-none focus-visible:outline-none',
            translucent ? 'bg-background/45 backdrop-blur-xl' : 'bg-background',
            className
          )}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            // Ensure focus moves to the drawer
            contentRef.current?.focus();
          }}
          tabIndex={-1}
        >
          {/* Visually hidden elements for accessibility - prevents Dialog warnings */}
          <VaulDrawer.Title style={visuallyHiddenStyles}>Navigation Menu</VaulDrawer.Title>
          <VaulDrawer.Description style={visuallyHiddenStyles}>
            Access navigation links and actions
          </VaulDrawer.Description>
          <div className='mx-auto w-12 h-1 shrink-0 rounded-full bg-muted mt-3 mb-2' />
          <div className='w-full h-full md:h-auto flex flex-col px-4 pb-2 md:pb-6 focus:outline-none overflow-hidden'>
            {children}
          </div>
        </VaulDrawer.Content>
      </VaulDrawer.Portal>
    </VaulDrawer.Root>
  );
}
